(() => {
  const SELECTOR_LIST = '[data-mt-partner-services-list]';
  const SELECTOR_ITEM = '[data-mt-partner-services-item]';
  const SELECTOR_INNER = '.mt-partner-services__item-inner';
  const SELECTOR_ENTER = '[data-mt-ps-enter]';
  const GAP_PX = 180;

  /** @param {number} n @param {number} min @param {number} max */
  function clamp(n, min, max) {
    return Math.min(Math.max(n, min), max);
  }

  function getTopOffsetPx() {
    const raw = getComputedStyle(document.body).getPropertyValue(
      '--header-group-height'
    );
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    return 80;
  }

  /**
   * @typedef {{
   *  item: HTMLElement,
   *  inner: HTMLElement,
   *  innerHeight: number
   * }} ItemMetric
   */

  /** @param {HTMLElement} listEl */
  function initList(listEl) {
    if (listEl.dataset.mtPsInit === '1') return;
    listEl.dataset.mtPsInit = '1';

    /** @type {HTMLElement[]} */
    const items = Array.from(listEl.querySelectorAll(SELECTOR_ITEM)).filter(
      (el) => el instanceof HTMLElement
    );

    /** @type {HTMLElement[]} */
    const inners = items
      .map((item) => item.querySelector(SELECTOR_INNER))
      .filter((el) => el instanceof HTMLElement);

    if (!items.length || inners.length !== items.length) return;

    // Enter animation: reveal headers when they enter viewport
    const enterTargets = Array.from(listEl.querySelectorAll(SELECTOR_ENTER)).filter(
      (el) => el instanceof HTMLElement
    );
    if (enterTargets.length) {
      const enterObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const target = entry.target;
            if (!(target instanceof HTMLElement)) return;
            target.classList.add('is-visible');
            observer.unobserve(target);
          });
        },
        {
          root: null,
          threshold: 0.15,
          rootMargin: '0px 0px -8% 0px'
        }
      );

      enterTargets.forEach((target) => enterObserver.observe(target));
    }

    // 后面的 block 盖住前面的 block
    inners.forEach((inner, index) => {
      inner.style.zIndex = String(index + 1);
      inner.style.position = 'relative';
    });

    /** @type {ItemMetric[]} */
    let metrics = [];
    let rafId = 0;
    let topOffset = 80;

    function computeTopVar() {
      topOffset = getTopOffsetPx();
      const hostSection = listEl.closest('.mt-partner-services');
      if (hostSection instanceof HTMLElement)
        hostSection.style.setProperty('--mt-ps-top', `${topOffset}px`);

      metrics = items
        .map((item, idx) => {
          const inner = inners[idx];
          if (!inner) return null;

          // 两个 item-inner 之间真实留白 180px
          item.style.paddingBottom = `${GAP_PX}px`;

          const innerHeight = inner.offsetHeight || 0;

          // 给 item 一个稳定的占位高度，避免 fixed 时内容跳动
          const minH = innerHeight + GAP_PX;
          if (minH > 0) item.style.minHeight = `${Math.ceil(minH)}px`;

          return { item, inner, innerHeight };
        })
        .filter((m) => m !== null);
    }

    function update() {
      rafId = 0;
      if (!metrics.length) return;

      const listRect = listEl.getBoundingClientRect();
      const viewportTop = 0;
      const viewportBottom = window.innerHeight || document.documentElement.clientHeight;

      // 如果整个列表已经完全离开视口（在上方或下方），全部恢复为正常流
      if (listRect.bottom <= viewportTop || listRect.top >= viewportBottom) {
        for (const m of metrics) {
          if (!m) continue;
          const inner = m.inner;
          inner.style.position = 'relative';
          inner.style.top = 'auto';
          inner.style.left = 'auto';
          inner.style.width = 'auto';
        }
        return;
      }

      // 计算当前“固定”的卡片：最后一个 top <= topOffset 的 item
      let activeIdx = -1;
      for (let i = 0; i < metrics.length; i++) {
        const mi = metrics[i];
        if (!mi) continue;
        const rectTop = mi.item.getBoundingClientRect().top;
        if (rectTop <= topOffset) activeIdx = i;
      }

      for (let i = 0; i < metrics.length; i++) {
        const m = metrics[i];
        if (!m) continue;
        const inner = m.inner;

        const isLast = i === metrics.length - 1;

        // 如果当前没有需要 fixed 的，或者已经滚过整个列表底部，则全部恢复为正常流
        if (activeIdx === -1 || listRect.bottom <= topOffset + m.innerHeight) {
          inner.style.position = 'relative';
          inner.style.top = 'auto';
          inner.style.left = 'auto';
          inner.style.width = 'auto';
          continue;
        }

        // 优化点：
        // - 只有“当前激活”的卡片会 fixed
        // - 最后一个卡片不再 fixed，它只是把上一个顶走，自己继续往上滑动
        if (i === activeIdx && !isLast) {
          const itemRect = m.item.getBoundingClientRect();
          inner.style.position = 'fixed';
          inner.style.top = `${topOffset}px`;
          inner.style.left = `${itemRect.left}px`;
          inner.style.width = `${itemRect.width}px`;
        } else {
          // 其他卡片正常流（relative），自然向上滚动并在 z-index 上覆盖 active
          inner.style.position = 'relative';
          inner.style.top = 'auto';
          inner.style.left = 'auto';
          inner.style.width = 'auto';
        }
      }
    }

    function requestUpdate() {
      if (rafId) return;
      rafId = window.requestAnimationFrame(update);
    }

    computeTopVar();
    update();

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener(
      'resize',
      () => {
        computeTopVar();
        update();
      },
      { passive: true }
    );
  }

  document.addEventListener('DOMContentLoaded', () => {
    const lists = document.querySelectorAll(SELECTOR_LIST);
    if (!lists.length) return;
    lists.forEach(initList);
  });

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const root = event.target || document;
      const lists = root.querySelectorAll ? root.querySelectorAll(SELECTOR_LIST) : [];
      if (!lists.length) return;
      lists.forEach(initList);
    });
  }
})();

